import { useState, useContext, useEffect } from 'react';
import { Box, Button, Stack, Badge } from '@mui/material';
import { ThemeContext } from '../Context';
import { SocketContext } from '../Context';
import { GuardiaContext } from '../Context';
import Summary from './Summary';
import Chat from './Chat';
import MyInsertions from './MyInsertions';
import GuardiaRight from './GuardiaRight';

// Partie droite (right) de l'application, elle affiche l'état actuel du procès-verbal modifié.
function Right() {
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { activeView, changeActiveView } = useContext(GuardiaContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);

  // Écouter les nouveaux messages
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data) => {
      // Si l'utilisateur n'est pas sur la page chat, marquer comme non lu
      if (activeView !== 'chat') {
        setHasUnreadMessages(true);
      }
    };

    socket.on('newMessage', handleNewMessage);

    return () => {
      socket.off('newMessage', handleNewMessage);
    };
  }, [socket, activeView]);

  const handleViewChange = (view) => {
    changeActiveView(view);

    // Si l'utilisateur clique sur Discussion, marquer les messages comme lus
    if (view === 'chat') {
      setHasUnreadMessages(false);
    }
  };

  // Ne pas afficher quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Barre de boutons fixée en haut */}
      <Box
        sx={{
          flexShrink: 0, // Empêche la barre de boutons de rétrécir
          zIndex: 10
        }}
      >
        <Stack 
          direction="row" 
          spacing={0} 
          sx={{ 
            background: isDark 
              ? 'rgb(40, 57, 73)' 
              : 'rgb(218, 230, 236)',
            boxShadow: isDark 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
          }}
        >
          <Button
            onClick={() => handleViewChange('summary')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'summary' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: activeView === 'summary' 
                ? 'primary.main' 
                : (isDark ? '#bdc3c7' : '#666'),
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                color: 'primary.main',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'summary' ? '60%' : '0',
                height: '2px',
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease',
              },
            }}
          >
            Sommaire
          </Button>

          
          <Button
            onClick={() => handleViewChange('myInsertions')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'myInsertions' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: activeView === 'myInsertions' 
                ? 'primary.main' 
                : (isDark ? '#bdc3c7' : '#666'),
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                color: 'primary.main',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'myInsertions' ? '60%' : '0',
                height: '2px',
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease',
              },
            }}
          >
            Insertions
          </Button>


          <Badge
            badgeContent=""
            variant="dot"
            color="error"
            invisible={!hasUnreadMessages}
            sx={{
              flex: 1, // Assure que le Badge prend la même largeur que les autres boutons
              '& .MuiBadge-dot': {
                minWidth: '10px',
                height: '10px',
                borderRadius: '50%',
                animation: hasUnreadMessages ? 'pulse 2s infinite' : 'none',
              },
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.2)',
                  opacity: 0.7,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }}
          >
            <Button
              onClick={() => handleViewChange('chat')}
              sx={{
                width: '100%', // Le bouton prend toute la largeur du Badge
                textTransform: 'none',
                fontWeight: activeView === 'chat' ? 600 : 500,
                py: 1,
                fontSize: '0.875rem',
                color: activeView === 'chat' 
                  ? 'primary.main' 
                  : (isDark ? '#bdc3c7' : '#666'),
                backgroundColor: 'transparent',
                borderRadius: 0,
                position: 'relative',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                  color: 'primary.main',
                },
                transition: 'all 0.2s ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: activeView === 'chat' ? '60%' : '0',
                  height: '2px',
                  backgroundColor: 'primary.main',
                  transition: 'width 0.3s ease',
                },
              }}
            >
              Discussion
            </Button>
          </Badge>

          <Button
            onClick={() => handleViewChange('guardia')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'guardia' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: isDark ? '#c084fc' : '#800080',
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(128, 0, 128, 0.15)' : 'rgba(128, 0, 128, 0.08)',
                color: isDark ? '#c084fc' : '#800080',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'guardia' ? '60%' : '0',
                height: '2px',
                backgroundColor: isDark ? '#c084fc' : '#800080',
                transition: 'width 0.3s ease',
              },
            }}
          >
            GuardIA
          </Button>
        </Stack>
      </Box>

      {/* Zone de contenu défilable */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: isDark ? '#1a252f' : 'inherit',
          // Styles personnalisés pour les scrollbars en mode sombre
          '&::-webkit-scrollbar': {
            width: isDark ? '8px' : undefined,
            height: isDark ? '8px' : undefined,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: isDark ? '#22303e' : undefined,
            borderRadius: isDark ? '4px' : undefined,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? '#2d3f4c' : undefined,
            borderRadius: isDark ? '4px' : undefined,
            '&:hover': {
              backgroundColor: isDark ? '#384550' : undefined,
            },
          },
          // Pour Firefox
          scrollbarWidth: isDark ? 'thin' : undefined,
          scrollbarColor: isDark ? '#2d3f4c #22303e' : undefined,
        }}
      >
        {/* Tous les composants sont toujours montés pour préserver les états, mais masqués visuellement */}
        <Box sx={{ display: activeView === 'summary' ? 'block' : 'none', height: '100%' }}>
          <Summary />
        </Box>
        <Box sx={{ display: activeView === 'chat' ? 'block' : 'none', height: '100%' }}>
          <Chat />
        </Box>
        <Box sx={{ display: activeView === 'myInsertions' ? 'block' : 'none', height: '100%' }}>
          <MyInsertions />
        </Box>
        <Box sx={{ display: activeView === 'guardia' ? 'block' : 'none', height: '100%' }}>
          <GuardiaRight />
        </Box>
      </Box>
    </Box>
  );
}

export default Right;



